import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SkipForward, Pause, Play, Music } from 'lucide-react';
import FullscreenButton from '@/components/FullscreenButton';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Spotify Web Playback SDK script loader
const loadSpotifySDK = () => {
  return new Promise((resolve) => {
    if (window.Spotify) {
      resolve(window.Spotify);
      return;
    }
    
    window.onSpotifyWebPlaybackSDKReady = () => {
      resolve(window.Spotify);
    };
    
    const script = document.createElement('script');
    script.src = 'https://sdk.scdn.co/spotify-player.js';
    script.async = true;
    document.body.appendChild(script);
  });
};

export default function BackgroundMusicPage() {
  const [pinEntered, setPinEntered] = useState(false);
  const [pin, setPin] = useState('');
  
  // Spotify state
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [spotifyToken, setSpotifyToken] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [deviceId, setDeviceId] = useState(null);
  
  // Page title
  usePageTitle('Background Music');
  
  // Playback state
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.20); // 20% default
  
  // Coordination state
  const [shouldPause, setShouldPause] = useState(false);
  const [pauseReason, setPauseReason] = useState(null);
  const [isFading, setIsFading] = useState(false);
  const wasPlayingBeforePause = useRef(false);
  
  // Refs for stable access in polling (avoid stale closures)
  const shouldPauseRef = useRef(false);
  const pauseReasonRef = useRef(null);
  const isPlayingRef = useRef(false);
  
  // Keep refs in sync with state
  useEffect(() => { shouldPauseRef.current = shouldPause; }, [shouldPause]);
  useEffect(() => { pauseReasonRef.current = pauseReason; }, [pauseReason]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  
  // Refs
  const playerRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const resumeTimeoutRef = useRef(null);
  const competitionId = 'global-background';

  // Check for stored tokens on load
  useEffect(() => {
    const storedToken = localStorage.getItem('spotify_access_token');
    const storedRefresh = localStorage.getItem('spotify_refresh_token');
    const tokenExpires = localStorage.getItem('spotify_token_expires');
    
    if (storedToken && storedRefresh) {
      if (tokenExpires && Date.now() > parseInt(tokenExpires)) {
        refreshAccessToken(storedRefresh);
      } else {
        setSpotifyToken(storedToken);
        setSpotifyConnected(true);
      }
    }
  }, []);

  const refreshAccessToken = async (refresh) => {
    try {
      const response = await axios.post(`${API}/spotify/refresh`, { refresh_token: refresh });
      const { access_token, expires_in } = response.data;
      localStorage.setItem('spotify_access_token', access_token);
      localStorage.setItem('spotify_token_expires', Date.now() + (expires_in * 1000));
      setSpotifyToken(access_token);
      setSpotifyConnected(true);
    } catch (err) {
      localStorage.removeItem('spotify_access_token');
      localStorage.removeItem('spotify_refresh_token');
      setSpotifyConnected(false);
    }
  };

  const connectSpotify = async () => {
    setIsConnecting(true);
    try {
      // Pass the current origin so backend knows where to redirect back to
      const origin = window.location.origin;
      const response = await axios.get(`${API}/spotify/auth-url?origin=${encodeURIComponent(origin)}`);
      window.location.href = response.data.auth_url;
    } catch (err) {
      toast.error('Failed to connect to Spotify');
      setIsConnecting(false);
    }
  };

  // Initialize Spotify Web Playback SDK
  useEffect(() => {
    if (!pinEntered || !spotifyConnected || !spotifyToken) return;

    const initializePlayer = async () => {
      try {
        const Spotify = await loadSpotifySDK();
        
        const player = new Spotify.Player({
          name: 'Scorz Background Music',
          getOAuthToken: async (cb) => {
            // Check if token needs refresh
            const tokenExpires = localStorage.getItem('spotify_token_expires');
            if (tokenExpires && Date.now() > parseInt(tokenExpires) - 60000) {
              const refresh = localStorage.getItem('spotify_refresh_token');
              if (refresh) {
                try {
                  const response = await axios.post(`${API}/spotify/refresh`, { refresh_token: refresh });
                  const { access_token, expires_in } = response.data;
                  localStorage.setItem('spotify_access_token', access_token);
                  localStorage.setItem('spotify_token_expires', Date.now() + (expires_in * 1000));
                  setSpotifyToken(access_token);
                  cb(access_token);
                  return;
                } catch (e) {
                  console.error('Token refresh failed:', e);
                }
              }
            }
            cb(spotifyToken);
          },
          volume: volume
        });

        // Error handling
        player.addListener('initialization_error', ({ message }) => {
          console.error('Initialization error:', message);
          toast.error('Failed to initialize Spotify player');
        });

        player.addListener('authentication_error', ({ message }) => {
          console.error('Authentication error:', message);
          toast.error('Spotify authentication failed');
          setSpotifyConnected(false);
          localStorage.removeItem('spotify_access_token');
        });

        player.addListener('account_error', ({ message }) => {
          console.error('Account error:', message);
          toast.error('Spotify Premium required for Web Playback');
        });

        player.addListener('playback_error', ({ message }) => {
          console.error('Playback error:', message);
        });

        // Playback status updates
        player.addListener('player_state_changed', (state) => {
          if (!state) return;
          
          const track = state.track_window.current_track;
          setCurrentTrack(track);
          setIsPlaying(!state.paused);
          setProgress(state.position);
          setDuration(state.duration);
        });

        // Ready
        player.addListener('ready', ({ device_id }) => {
          console.log('Spotify player ready with device ID:', device_id);
          setDeviceId(device_id);
          setSdkReady(true);
          toast.success('Spotify player ready');
          
          // Store player globally for floor music coordination
          window.spotifyPlayer = player;
          window.spotifyDeviceId = device_id;
        });

        // Not Ready
        player.addListener('not_ready', ({ device_id }) => {
          console.log('Device has gone offline:', device_id);
          setSdkReady(false);
        });

        // Connect to the player
        const connected = await player.connect();
        if (connected) {
          playerRef.current = player;
          console.log('Connected to Spotify Web Playback SDK');
        } else {
          toast.error('Failed to connect to Spotify');
        }
      } catch (error) {
        console.error('Error initializing Spotify player:', error);
        toast.error('Failed to initialize Spotify player');
      }
    };

    initializePlayer();

    return () => {
      if (playerRef.current) {
        playerRef.current.disconnect();
        playerRef.current = null;
        window.spotifyPlayer = null;
        window.spotifyDeviceId = null;
      }
    };
  }, [pinEntered, spotifyConnected, spotifyToken]);

  // Update progress locally when playing
  useEffect(() => {
    if (isPlaying && !shouldPause) {
      progressIntervalRef.current = setInterval(() => {
        setProgress(p => Math.min(p + 1000, duration));
      }, 1000);
    } else {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    }
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [isPlaying, shouldPause, duration]);

  // Fade out Spotify (before floor music/announcement)
  const fadeOut = useCallback(async (onComplete) => {
    const player = playerRef.current;
    if (!player) { if (onComplete) onComplete(); return; }
    
    setIsFading(true);
    let vol = volume;
    
    const fadeInterval = setInterval(async () => {
      vol -= 0.02;
      
      if (vol <= 0) {
        clearInterval(fadeInterval);
        await player.pause();
        setIsPlaying(false);
        setIsFading(false);
        if (onComplete) onComplete();
      } else {
        await player.setVolume(vol);
      }
    }, 50);
  }, [volume]);

  // Fade in Spotify (after floor music/announcement)
  const fadeIn = useCallback(async () => {
    const player = playerRef.current;
    if (!player) return;
    
    setIsFading(true);
    await player.resume();
    setIsPlaying(true);
    
    let vol = 0;
    const fadeInterval = setInterval(async () => {
      vol += 0.02;
      
      if (vol >= volume) {
        clearInterval(fadeInterval);
        await player.setVolume(volume);
        setIsFading(false);
      } else {
        await player.setVolume(vol);
      }
    }, 50);
  }, [volume]);

  // Toggle play/pause
  const togglePlayPause = async () => {
    const player = playerRef.current;
    if (!player) return;
    
    if (isPlaying) {
      await player.pause();
    } else {
      await player.resume();
    }
  };

  // Skip to next track
  const skipTrack = async () => {
    const player = playerRef.current;
    if (!player) return;
    await player.nextTrack();
  };

  // Poll for pause signals from floor music - BULLETPROOF VERSION
  useEffect(() => {
    if (!pinEntered || !spotifyConnected || !sdkReady) return;

    let lastShouldPause = null;
    let resumeTimer = null;
    let wasPlaying = false;

    const pollState = async () => {
      try {
        const response = await axios.get(`${API}/music/background-state/${competitionId}`);
        const serverState = response.data;
        const player = playerRef.current;
        
        // First poll - just sync state
        if (lastShouldPause === null) {
          lastShouldPause = serverState.shouldPause;
          setShouldPause(serverState.shouldPause);
          setPauseReason(serverState.pauseReason);
          console.log('[BG] Init:', serverState.shouldPause);
          return;
        }
        
        // PAUSE: Server says pause, we weren't paused before
        if (serverState.shouldPause && !lastShouldPause) {
          console.log('[BG] PAUSING - floor music started');
          lastShouldPause = true;
          
          if (resumeTimer) {
            clearTimeout(resumeTimer);
            resumeTimer = null;
          }
          
          // Check DIRECTLY from Spotify player if playing
          if (player) {
            try {
              const state = await player.getCurrentState();
              wasPlaying = state && !state.paused;
              console.log('[BG] Was playing:', wasPlaying);
            } catch (e) {
              wasPlaying = isPlayingRef.current;
            }
          }
          
          setShouldPause(true);
          setPauseReason(serverState.pauseReason);
          
          // Fade out
          if (player && wasPlaying) {
            let vol = volume;
            const fade = setInterval(async () => {
              vol -= 0.03;
              if (vol <= 0) {
                clearInterval(fade);
                try { await player.pause(); setIsPlaying(false); } catch (e) {}
              } else {
                try { await player.setVolume(Math.max(0, vol)); } catch (e) {}
              }
            }, 40);
          }
        }
        
        // RESUME: Server says resume, we were paused
        if (!serverState.shouldPause && lastShouldPause) {
          console.log('[BG] ENDED - will resume, wasPlaying:', wasPlaying);
          lastShouldPause = false;
          
          setShouldPause(false);
          setPauseReason(null);
          
          if (wasPlaying && player) {
            if (resumeTimer) clearTimeout(resumeTimer);
            
            resumeTimer = setTimeout(async () => {
              console.log('[BG] RESUMING NOW');
              resumeTimer = null;
              try {
                await player.resume();
                setIsPlaying(true);
                let vol = 0;
                const fade = setInterval(async () => {
                  vol += 0.03;
                  if (vol >= volume) {
                    clearInterval(fade);
                    try { await player.setVolume(volume); } catch (e) {}
                  } else {
                    try { await player.setVolume(vol); } catch (e) {}
                  }
                }, 40);
              } catch (e) {
                console.error('[BG] Resume failed:', e);
              }
            }, 2000);
          }
        }
        
        lastShouldPause = serverState.shouldPause;
      } catch (err) {}
    };

    pollState();
    const interval = setInterval(pollState, 500);
    return () => {
      clearInterval(interval);
      if (resumeTimer) clearTimeout(resumeTimer);
    };
  }, [pinEntered, spotifyConnected, sdkReady, volume]);

  // Format time
  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, []);

  // PIN screen
  if (!pinEntered) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <img src="/scorz-logo.png" alt="Scorz" className="h-20 mx-auto mb-12" />
          <p className="text-gray-500 text-sm mb-8">Background Music</p>
          <form onSubmit={(e) => {
            e.preventDefault();
            if (pin === '2507') {
              setPinEntered(true);
            } else {
              toast.error('Invalid PIN');
              setPin('');
            }
          }}>
            <Input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter PIN"
              className="text-center text-2xl tracking-[0.3em] bg-gray-50 border-gray-200 rounded-lg text-black placeholder:text-gray-400 focus:border-gray-400 focus:ring-0 py-4"
              maxLength={4}
              autoFocus
              data-testid="background-music-pin-input"
            />
            <Button 
              type="submit" 
              className="mt-6 w-full bg-black text-white hover:bg-gray-800 rounded-lg py-3"
              data-testid="background-music-submit-btn"
            >
              Access
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Connect to Spotify screen
  if (!spotifyConnected) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <img src="/scorz-logo.png" alt="Scorz" className="h-20 mx-auto mb-12" />
          <p className="text-gray-500 text-sm mb-8">Connect to display now playing</p>
          <Button 
            onClick={connectSpotify}
            disabled={isConnecting}
            className="w-full bg-black text-white hover:bg-gray-800 rounded-lg py-4 text-base font-medium"
          >
            {isConnecting ? 'Connecting...' : 'Connect Spotify'}
          </Button>
          <p className="text-gray-400 text-xs mt-4">Requires Spotify Premium</p>
        </div>
      </div>
    );
  }

  // SDK Loading screen
  if (!sdkReady) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <img src="/scorz-logo.png" alt="Scorz" className="h-20 mx-auto mb-12" />
          <div className="w-12 h-12 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Initializing Spotify player...</p>
          <p className="text-gray-400 text-sm mt-2">This may take a few seconds</p>
        </div>
      </div>
    );
  }

  const albumArt = currentTrack?.album?.images?.[0]?.url;
  const artistNames = currentTrack?.artists?.map(a => a.name).join(', ');
  const progressPercent = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
      {/* Pause Overlay */}
      {shouldPause && (
        <div className="fixed inset-0 bg-white/95 z-50 flex items-center justify-center">
          <div className="text-center">
            <Pause className="w-16 h-16 mx-auto mb-6 text-gray-300" strokeWidth={1.5} />
            <p className="text-xl text-gray-400 font-light">
              {pauseReason === 'floor_music' ? 'Floor Music Playing' : 
               pauseReason === 'announcement' ? 'Announcement' : 'Paused'}
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="p-6 flex items-center justify-between border-b border-gray-100">
        <div></div>
        <img src="/scorz-logo.png" alt="Scorz" className="h-8" />
        <FullscreenButton />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        {currentTrack ? (
          <>
            {/* Album Art */}
            {albumArt && (
              <div className="mb-8">
                <img 
                  src={albumArt} 
                  alt="Album Art" 
                  className="w-56 h-56 rounded-xl shadow-lg"
                />
              </div>
            )}

            {/* Track Info */}
            <div className="text-center mb-8 max-w-md">
              <h1 className="text-2xl font-semibold mb-1 truncate">{currentTrack.name}</h1>
              <p className="text-lg text-gray-500 truncate">{artistNames}</p>
            </div>

            {/* Progress Bar */}
            <div className="w-full max-w-sm mb-8">
              <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-black transition-all duration-1000 ease-linear"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-sm text-gray-400">
                <span>{formatTime(progress)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-6">
              <button 
                onClick={togglePlayPause}
                className="w-14 h-14 rounded-full bg-black flex items-center justify-center hover:bg-gray-800 transition-colors"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6 text-white" strokeWidth={2} />
                ) : (
                  <Play className="w-6 h-6 text-white ml-0.5" strokeWidth={2} />
                )}
              </button>

              <button 
                onClick={skipTrack} 
                className="p-3 rounded-full hover:bg-gray-100 transition-colors border border-gray-200"
                aria-label="Skip to next song"
              >
                <SkipForward className="w-6 h-6 text-black" strokeWidth={2} />
              </button>
            </div>
            
            {/* Instructions */}
            <div className="mt-8 text-center text-gray-400 text-sm">
              <p>Open Spotify app and select "Scorz Background Music" as device</p>
              <p className="mt-1">Then start a playlist from the Spotify app</p>
            </div>
          </>
        ) : (
          <div className="text-center">
            <div className="w-56 h-56 bg-gray-100 rounded-xl mb-8 flex items-center justify-center">
              <Music className="w-16 h-16 text-gray-300" />
            </div>
            <p className="text-gray-400 text-lg">No track playing</p>
            <p className="text-gray-300 text-sm mt-2">Open Spotify and select "Scorz Background Music"</p>
            <p className="text-gray-300 text-sm">Then start playing a playlist</p>
          </div>
        )}
      </div>

      {/* Footer Status */}
      <div className="p-4 text-center border-t border-gray-100">
        <div className="inline-flex items-center gap-2 text-xs text-gray-400">
          <div className={`w-1.5 h-1.5 rounded-full ${sdkReady ? 'bg-green-500' : 'bg-gray-300'}`} />
          <span>{sdkReady ? 'Player Ready' : 'Connecting...'}</span>
          {isPlaying && !shouldPause && (
            <>
              <span className="mx-1">•</span>
              <span>Playing</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
